import { Box } from "@crayon/design-system-react";
import FormAutocomplete, { FormAutocompleteOption } from "components/primitives/FormAutocomplete";
import useAutotaskBillingCodes from "hooks/api/useAutotaskBillingCodes";
import useAutotaskVendors from "hooks/api/useAutotaskVendors";
import useScreen from "hooks/useScreen";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ProgramEditFormSchemaType } from "types/FormSchemas/ProgramEditFormSchema";

interface AutotaskExtraBillingSettingsProps {
  isParentLoading: boolean;
  disabled: boolean;
  billingCodeId: string;
  vendorId: string;
}

const AutotaskExtraBillingSettings = (props: AutotaskExtraBillingSettingsProps) => {
  const { isParentLoading, disabled, billingCodeId, vendorId } = props;
  const { control, reset, getValues } = useFormContext<ProgramEditFormSchemaType>();
  const { billingCodes, isBillingCodesFetching } = useAutotaskBillingCodes();
  const { vendors, isVendorsFetching } = useAutotaskVendors();
  const { smMatch } = useScreen();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isParentLoading || isBillingCodesFetching || isVendorsFetching) return;

    const selectedCode = billingCodes?.items.find((x) => x.id.toString() === billingCodeId);
    const selectedVendor = vendors?.items.find((x) => x.id.toString() === vendorId);

    setTimeout(() => {
      reset({
        ...getValues(),
        ...(selectedCode && {
          autotaskBillingCode: {
            id: selectedCode.id.toString(),
            label: selectedCode.name,
          },
        }),
        ...(selectedVendor && {
          autotaskVendor: {
            id: selectedVendor.id.toString(),
            label: selectedVendor.companyName,
          },
        }),
      });
      setIsLoading(false);
    }, 200);
  }, [
    isParentLoading,
    billingCodes,
    isBillingCodesFetching,
    vendors,
    isVendorsFetching,
    reset,
    getValues,
    billingCodeId,
    vendorId,
  ]);

  const billingCodeOptions = useMemo<FormAutocompleteOption[]>(
    () => billingCodes?.items?.map((x) => ({ id: `${x.id}`, label: x.name })) ?? [],
    [billingCodes],
  );

  const vendorOptions = useMemo<FormAutocompleteOption[]>(
    () => vendors?.items?.map((x) => ({ id: `${x.id}`, label: x.companyName })) ?? [],
    [vendors],
  );

  return (
    <Box display="flex" gap={4} flexWrap="wrap">
      <FormAutocomplete
        control={control}
        bindSchemaFieldName="autotaskBillingCode"
        label="Billing Code"
        testId="Billing Code"
        options={billingCodeOptions}
        sxRoot={{ minWidth: 220, ...(!smMatch && { flex: 1 }) }}
        disabled={disabled}
        isLoading={isLoading}
        clearable
        required
      />

      <FormAutocomplete
        control={control}
        bindSchemaFieldName="autotaskVendor"
        label="Vendor"
        testId="Vendor"
        options={vendorOptions}
        sxRoot={{ minWidth: 220, ...(!smMatch && { flex: 1 }) }}
        disabled={disabled}
        isLoading={isLoading}
        clearable
      />
    </Box>
  );
};

export default AutotaskExtraBillingSettings;
